/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './MobileStatementsTable.scss';

import { string, object, oneOfType, arrayOf, element } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import Store from 'state/Store';
import { isEmpty, kebabCase, startCase } from 'lodash';

import {
  selectStatementsDocumentTypes,
  selectStatementsTimePeriod
} from 'state/statements/StatementsActions';
import { getCurrentOwnedAccounts } from 'state/account/AccountSelectors';
import { priorYear, currentYear } from 'services/Statements';
import { getDocumentAndDownload } from 'services/Rest';
import LoadingButton from 'components/shared/LoadingButton/LoadingButton';
import { statementsNotAvailableText } from 'services/Statements';

const COL_LABEL_NAME = 'name';
const COL_LABEL_TYPE = 'type';

const columns = [COL_LABEL_NAME, COL_LABEL_TYPE];

const TableColumns = ({ columns }) => (
  <tr className="columns">
    {columns.map((name) => (
      <th
        scope="col"
        className={`mobile-statements-table-column-${kebabCase(
          name
        )} e2e-mobile-statements-table-column-${kebabCase(
          name
        )} column-header-cell`}
        key={name}>
        {startCase(name)}
      </th>
    ))}
  </tr>
);

TableColumns.propTypes = {
  columns: arrayOf(oneOfType([element, string]))
};

const DocumentRows = ({
  accounts,
  document: {
    accountNumber,
    accountChecksum,
    documentId,
    name,
    productType,
    type,
    typeId
  } = {}
}) => (
  <Fragment>
    <td className="mobile-statements-table-statement-name">{name}</td>
    <td className="mobile-statements-table-pdf-cell">
      <LoadingButton
        className="link-button statements-mobile-button"
        onClick={() =>
          getDocumentAndDownload(
            accounts,
            accountNumber,
            accountChecksum,
            documentId,
            name,
            productType,
            typeId
          )
        }
        requestStatusKey={`documentDownload_${documentId}`}>
        {type}
      </LoadingButton>
    </td>
  </Fragment>
);

DocumentRows.propTypes = {
  document: object.isRequired
};

class MobileStatementsTable extends React.Component {
  componentDidMount() {
    const {
      query: {
        params: { documentFilter, year }
      },
      statementsFilteringSelections
    } = this.props;
    const { documentTypes } = statementsFilteringSelections;
    if (
      documentFilter &&
      documentTypes.options.find((o) => o.value === documentFilter)
    ) {
      if (year && year === 'prior') {
        Store.dispatch(selectStatementsTimePeriod(priorYear.toString()));
      } else {
        //Default to current year if year is not supplied in the URL because 2 out of the 3 doc types are mainly relevant for current year
        Store.dispatch(selectStatementsTimePeriod(currentYear.toString()));
      }
      Store.dispatch(selectStatementsDocumentTypes(documentFilter));
    }
  }

  render() {
    const {
      accounts,
      dateGroups,
      statementsFilteringSelections,
      hasAlaska529Only
    } = this.props;
    const { documentTypes, timePeriod, productTypes } =
      statementsFilteringSelections;
    const statementUnavailableText = statementsNotAvailableText(
      documentTypes,
      timePeriod,
      productTypes,
      hasAlaska529Only
    );

    return (
      <div className="mobile-statements-table-container">
        {isEmpty(dateGroups) ? (
          <div className="mobile-statements-table-no-statements">
            {statementUnavailableText}
          </div>
        ) : (
          <table className="mobile-statements-table">
            <thead>
              <TableColumns columns={columns} />
            </thead>
            <tbody>
              {dateGroups.map((dateGroups, idx) => {
                const [date, rows] = Object.entries(dateGroups)[0];
                return (
                  <Fragment key={idx}>
                    <tr className="mobile-statements-table-row-document-date">
                      <td
                        className="mobile-statements-table-statement-date"
                        colSpan={2}>
                        {date}
                      </td>
                    </tr>
                    {rows.map((document, r) => (
                      <tr
                        className="mobile-statements-table-row-document-details"
                        key={date + r}>
                        <DocumentRows accounts={accounts} document={document} />
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }
}

MobileStatementsTable.propTypes = {
  dateGroups: arrayOf(object).isRequired
};

const mapStateToProps = (state) => ({
  accounts: getCurrentOwnedAccounts(state),
  query: state.session.query,
  statementsFilteringSelections: state.statementsFilteringSelections,
  hasAlaska529Only: state.view.hasAlaska529Only
});

export default connect(mapStateToProps)(MobileStatementsTable);
